import {
  Box,
  Button,
  FontColorEnum,
  FormHint,
  SearchIcon,
  Typography,
} from '@acceso/design-system';
import { filesize } from 'filesize';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isFileAccepted } from '@/utils/files';

type FilePickerProps = {
  value: File | File[];
  onChange: (value: File[]) => void;
  accept?: string;
  format?: string;

  /**
   * Maximum size of the files in bytes.
   */
  maxSize?: number;
  addFiles?: boolean;

  /**
   * @default false
   */
  singleFile?: boolean;
};

export function FilesPicker({
  value,
  onChange,
  accept,
  format,
  maxSize,
  singleFile = false,
  addFiles,
}: FilePickerProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Files the picker refused. Without this the field just stays empty and the
  // user is left guessing why the file they chose did not appear.
  const [rejectionMessages, setRejectionMessages] = useState<string[]>([]);
  const parsedValue = Array.isArray(value) ? value : [value];
  const files = value ? parsedValue : [];

  const selectedFilesLabel = singleFile
    ? files.at(0)?.name
    : t('{{count}} archivos seleccionados', { count: files.length });

  const pickFile = () => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  };

  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [value]);

  const rejectFiles = (rejectedByType: File[], rejectedBySize: File[]) => {
    const messages: string[] = [];
    const names = (rejected: File[]) => rejected.map(file => file.name).join(', ');

    if (rejectedByType.length > 0) {
      messages.push(
        format
          ? t('No se ha podido añadir {{files}}: el formato válido es {{format}}', {
              files: names(rejectedByType),
              format,
            })
          : t('No se ha podido añadir {{files}}: el formato no es válido', {
              files: names(rejectedByType),
            })
      );
    }

    if (rejectedBySize.length > 0 && maxSize !== undefined) {
      messages.push(
        t('No se ha podido añadir {{files}}: supera el tamaño máximo de {{maxSize}}', {
          files: names(rejectedBySize),
          maxSize: filesize(maxSize, { standard: 'iec' }),
        })
      );
    }

    setRejectionMessages(messages);
  };

  return (
    <Box className="flex w-full flex-col gap-2">
      <Box className="gap flex h-[48px] w-full items-center gap-2 border-b border-stroke-30-0 bg-white p-1 hover:border-stroke-60-0">
        <Button className="bg-accent-active-primary-30" leftIcon={SearchIcon} onClick={pickFile}>
          {t('Archivo')}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple
          onChange={event => {
            const newFiles = event.currentTarget.files;

            if (!newFiles) {
              setRejectionMessages([]);
              onChange([]);
              return;
            }

            const validNewFiles: File[] = [];
            const rejectedByType: File[] = [];
            const rejectedBySize: File[] = [];

            Array.from(newFiles).forEach(file => {
              if (!isFileAccepted(file, accept)) {
                rejectedByType.push(file);
                return;
              }

              if (maxSize !== undefined && file.size > maxSize) {
                rejectedBySize.push(file);
                return;
              }

              validNewFiles.push(file);
            });

            rejectFiles(rejectedByType, rejectedBySize);
            onChange(addFiles ? [...files, ...validNewFiles] : validNewFiles);
          }}
        />

        <Typography className="text-text-medium-10">
          {files.length > 0 && selectedFilesLabel ? selectedFilesLabel : t('Ningún archivo')}
        </Typography>
      </Box>

      {format && maxSize && (
        <FormHint>
          {t('Formato válido: {{format}} con un máximo de {{maxSize}}', {
            format,
            maxSize: filesize(maxSize, { standard: 'iec' }),
          })}
        </FormHint>
      )}

      {rejectionMessages.map(message => (
        <FormHint key={message} color={FontColorEnum.Error20}>
          {message}
        </FormHint>
      ))}
    </Box>
  );
}
